import { safeStorageGetTolerant, safeStorageSet } from '../storage/safe-storage.js';

const STORAGE_KEY = 'adminsr_theme';

/**
 * Centralized theme preference manager.
 * Reads/writes theme preference (dark or light), sets `data-theme` on <html>.
 * Default is light.
 *
 * @returns {{ get(): 'light'|'dark', set(pref: 'light'|'dark'): void, subscribe(fn: (theme: 'light'|'dark') => void): () => void, destroy(): void }}
 */
export function createThemeManager() {
  /** @type {Array<(theme: 'light'|'dark') => void>} */
  let subscribers = [];
  let preference = readStoredPreference();

  // Apply on creation (handles case where inline script already set data-theme).
  applyToDOM();

  function readStoredPreference() {
    // Format-tolerant via safeStorageGetTolerant: handles both JSON-stringified
    // ("\"dark\"") and plain string ("dark") — must match index.html inline script.
    const stored = safeStorageGetTolerant(STORAGE_KEY, null);
    if (stored === 'light' || stored === 'dark') return stored;
    return 'light';
  }

  function applyToDOM() {
    document.documentElement.setAttribute('data-theme', preference);
  }

  function notify() {
    for (const fn of subscribers) fn(preference);
  }

  return {
    get() {
      return preference;
    },

    set(pref) {
      if (pref === preference) return;
      preference = pref;
      safeStorageSet(STORAGE_KEY, pref);
      applyToDOM();
      notify();
    },

    subscribe(fn) {
      subscribers.push(fn);
      return () => {
        subscribers = subscribers.filter((s) => s !== fn);
      };
    },

    destroy() {
      subscribers = [];
    },
  };
}
